/*
  RSoC unit gate (ABE-850) — defer injectable-unit placement until RSoC resolves.

  RSoC is primary revenue on content-site article pages. Injectable units
  (yahoo_cta, yahoo_ads, dynamic_creative, ...) are placed by
  injectable-units.ts right after the tracking registry kicks off RSoC, so a
  widget can load while RSoC is still resolving and compete with it. When
  enabled, this gate holds placement until every RSoC container on the page has
  called back (google-rsoc dispatches `rampJsUnitLoaded(container, "csa")` from
  its adLoadedCallback), with a timeout fallback so a silent RSoC never blocks
  widget revenue permanently.

  This is the publisher-injection counterpart of the renderer's SSR-only
  `delay_algo_results` mechanism, and shares its 3000ms default.

  Config (PAPI-Core response, per segment, no release needed to toggle):

    render_options.rampjs.defer_units_until_rsoc          boolean (default false)
    render_options.rampjs.defer_units_until_rsoc_timeout  number ms (default 3000)

  The gate releases immediately when there is nothing to wait for — gate off,
  no injectable units, or no RSoC on the page — and early when the RSoC
  containers are missing from the DOM or the CSA script fails to load, since
  Google never calls back in those cases.

  Measurement: getUnitGateDiagnostics() is stamped onto every RSoC pingback
  (google-rsoc buildPingbackUrl) so RSoC RPM can be split gated vs ungated on
  pages that carry injectable units.
*/

import { CONSOLE_CAPTURE_PREFIX } from "@ascent/debugger";
import type * as PAPICore from "@ascent/papi-core";
import type { Response } from "@rampjs/server";
import { onDomReady } from "./util";

export const DEFAULT_UNIT_GATE_TIMEOUT_MS = 3000;

// QA override (debug bundle only): `?rampjs_defer_units=1` forces the gate on,
// `=0` forces it off, regardless of the response config.
export const DEFER_UNITS_OVERRIDE_PARAM = "rampjs_defer_units";

export type UnitGateOutcome =
  /** Every RSoC container called back. */
  | "rsoc_loaded"
  /** No RSoC containers exist (absent from the response or the DOM). */
  | "no_rsoc"
  /** The CSA script failed to load; no callback will ever arrive. */
  | "rsoc_failed"
  /** RSoC did not resolve within the configured timeout. */
  | "timeout";

export interface UnitGateResult {
  outcome: UnitGateOutcome;
  /** ms between gate creation and release. */
  waitedMs: number;
}

export interface UnitGateConfig {
  enabled: boolean;
  timeoutMs: number;
}

// Pingback diagnostics for the current page; see getUnitGateDiagnostics.
let diagnostics: Record<string, string> = {};

/**
 * Read the gate config from the response. The timeout falls back to the
 * default for anything other than a positive finite number.
 */
export function getUnitGateConfig(server: Response): UnitGateConfig {
  const rampjs = server?.search?.response?.render_options?.rampjs;
  let enabled = rampjs?.defer_units_until_rsoc === true;
  const timeout = rampjs?.defer_units_until_rsoc_timeout;
  const timeoutMs =
    typeof timeout === "number" && Number.isFinite(timeout) && timeout > 0
      ? timeout
      : DEFAULT_UNIT_GATE_TIMEOUT_MS;

  if (__DEBUG__) {
    const override = new URLSearchParams(window.location.search).get(
      DEFER_UNITS_OVERRIDE_PARAM,
    );
    if (override === "1" || override === "true") enabled = true;
    if (override === "0" || override === "false") enabled = false;
  }

  return { enabled, timeoutMs };
}

/**
 * RSoC containers the page expects a Google callback for. Mirrors the unit
 * list google-rsoc.onResponse hands to `_googCsa` — which is only called when
 * `csa_page_options` is present, so without it no callback can fire.
 */
function getRsocContainers(server: Response): Set<string> {
  const containers = new Set<string>();
  const response = server?.search?.response;
  const rsoc = response?.page_options?.rsoc;
  if (!rsoc?.csa_page_options || !Array.isArray(response?.regions)) {
    return containers;
  }
  for (const region of response.regions) {
    for (const group of region.groups ?? []) {
      // Same predicate as google-rsoc isGroup (not imported: google-rsoc reads
      // this module's diagnostics, so importing back would be circular).
      if (group.backend !== "google" || group.result_type !== "rsoc") continue;
      const params = group.unit_level_params as
        | PAPICore.UnitLevelParams
        | undefined;
      const container = params?.container;
      if (typeof container === "string" && container) {
        containers.add(container);
      }
    }
  }
  return containers;
}

/**
 * Extra params for RSoC pingbacks. Empty on pages without injectable units, so
 * existing pingbacks are unchanged; otherwise `units_present=1` plus
 * `units_deferred=1|0` (whether the gate actually held placement).
 */
export function getUnitGateDiagnostics(): Record<string, string> {
  return diagnostics;
}

/**
 * Create the unit gate for this page. Call BEFORE RSoC is initialized so no
 * callback (or missing-div / script-failure pingback) can slip past it.
 *
 * Returns `undefined` when there is nothing to wait for (gate disabled, no
 * injectable units, or no RSoC on the page) — the caller places units
 * synchronously, exactly as before. Otherwise returns a promise that resolves
 * (never rejects) once units may be placed.
 */
export function createUnitGate(
  server: Response,
): Promise<UnitGateResult> | undefined {
  const hasUnits = Array.isArray(server?.units) && server.units.length > 0;
  if (!hasUnits) {
    diagnostics = {};
    return undefined;
  }

  const { enabled, timeoutMs } = getUnitGateConfig(server);
  const pending = enabled ? getRsocContainers(server) : new Set<string>();
  const deferred = pending.size > 0;
  diagnostics = { units_present: "1", units_deferred: deferred ? "1" : "0" };

  if (__DEBUG__ && enabled && !deferred) {
    console.log(
      `${CONSOLE_CAPTURE_PREFIX} Unit gate: enabled but no RSoC on page; placing units immediately.`,
    );
  }
  if (!deferred) return undefined;

  const startedAt = Date.now();
  let received = 0;

  return new Promise<UnitGateResult>((resolve) => {
    let settled = false;

    const release = (outcome: UnitGateOutcome) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener("rampJsUnitLoaded", onUnitLoaded);
      window.removeEventListener("rampJsRsocFailed", onRsocFailed);
      const result = { outcome, waitedMs: Date.now() - startedAt };
      if (__DEBUG__) {
        console.log(`${CONSOLE_CAPTURE_PREFIX} Unit gate released:`, result);
      }
      resolve(result);
    };

    const onUnitLoaded = (event: Event) => {
      const detail = (
        event as CustomEvent<{ container: string; adType: string }>
      ).detail;
      if (detail?.adType !== "csa" || !pending.delete(detail.container)) return;
      received++;
      if (pending.size === 0) release("rsoc_loaded");
    };

    const onRsocFailed = () => release("rsoc_failed");

    const timer = setTimeout(() => release("timeout"), timeoutMs);
    window.addEventListener("rampJsUnitLoaded", onUnitLoaded);
    window.addEventListener("rampJsRsocFailed", onRsocFailed);

    // Google never calls back for a container that isn't in the DOM, so stop
    // waiting on those once the document has parsed.
    onDomReady(() => {
      if (settled) return;
      for (const container of [...pending]) {
        if (!document.getElementById(container)) pending.delete(container);
      }
      if (pending.size === 0) release(received > 0 ? "rsoc_loaded" : "no_rsoc");
    });
  });
}
